import { HTMLAttributes } from 'react'
import lowerCase from 'lodash/lowerCase'
import Link from 'next/link'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Logo from '@/components/base/Logo'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'

export type FooterProps = HTMLAttributes<HTMLElement>

const Footer = ({ className, ...props }: FooterProps) => {
  const { strings } = useStore()

  return (
    <footer
      className={cn(['relative z-10 bg-primary-hover text-white', className])}
      {...props}
    >
      <Container
        className={cn(['relative z-10 py-[3.2rem]', 'md:py-[6.4rem]'])}
      >
        <Grid className="gap-y-[2rem] md:gap-y-[4rem]">
          {/* First Column */}
          <div
            className={cn([
              'col-span-4 flex items-center justify-center',
              'md:col-span-2 md:justify-start',
              'lg:col-span-2 lg:col-start-2'
            ])}
          >
            <Link href={strings?.header_settings?.website_url} target="_blank">
              <Logo theme="dark" />
            </Link>
          </div>
          {/* Second Column */}
          <div
            className={cn([
              'col-span-4 flex flex-col items-center justify-center gap-x-[5rem] gap-y-[1rem]',
              'md:col-span-9 md:col-start-4 md:flex-row md:justify-end',
              'lg:col-span-4 lg:col-start-4 lg:justify-start'
            ])}
          >
            <ul className="flex gap-[1rem]">
              {strings?.footer_settings?.footer_links?.map(
                (item: any, index: any) => (
                  <li key={index}>
                    <Link
                      href={item.url}
                      target={item.target}
                      className={cn([
                        'transition-colors duration-300 ease-out-cubic',
                        'underline hover:text-white/80'
                      ])}
                      scroll={false}
                    >
                      <Typography variant="footer">{item.title}</Typography>
                    </Link>
                  </li>
                )
              )}
            </ul>
            {strings?.footer_settings?.copyright ? (
              <Typography variant="footer">
                {strings.footer_settings.copyright}
              </Typography>
            ) : null}
          </div>
          {/* Third Column */}
          <div
            className={cn([
              'col-span-4 flex justify-center',
              'md:col-span-12',
              'lg:col-span-4 lg:col-start-8 lg:justify-end'
            ])}
          >
            <ul
              className={cn([
                'grid w-full grid-cols-3 gap-[2rem]',
                'md:flex md:w-auto md:gap-[1rem]'
              ])}
            >
              {strings?.footer_settings?.social_links?.map(
                (item: any, index: any) => (
                  <li
                    key={index}
                    className={cn(['flex justify-center', 'md:block'])}
                  >
                    <a
                      href={item.url}
                      target={item.target}
                      className={cn([
                        'flex size-[5rem] items-center justify-center rounded-full border-2 border-white/20',
                        'transition-colors duration-300 ease-out-cubic hover:border-white',
                        'group'
                      ])}
                    >
                      <SVGIcon
                        symbol={`${lowerCase(item.title)}-icon`}
                        className={cn([
                          'h-[1.2rem] w-full shrink-0 fill-white/80',
                          'transition-colors duration-300 ease-out-cubic group-hover:fill-white'
                        ])}
                      />
                    </a>
                  </li>
                )
              )}
            </ul>
          </div>
        </Grid>
      </Container>
    </footer>
  )
}

export default Footer
